import { Controller } from "@hotwired/stimulus"

// Owns the pantry scan upload form: shows thumbnail previews when a photo
// is selected, swaps the CTA copy ("Tap to take a photo" → "Scan 2 photos →"),
// and flips to a full-bleed loading overlay on submit so the 3-8s wait for
// Claude doesn't look like the page crashed.
const LOADING_STEPS = [
  { text: "Looking at your fridge…", delay: 0 },
  { text: "Identifying items…",      delay: 2500 },
  { text: "Matching ingredients…",   delay: 5500 },
  { text: "Almost there…",           delay: 9000 },
  { text: "Still working — hang tight…", delay: 14000 }
]

export default class extends Controller {
  static targets = [
    "input", "placeholder", "previews", "submit", "submitLabel", "overlay", "overlayText", "slow"
  ]

  select() {
    const files = Array.from(this.inputTarget.files || [])
    this.clearPreviews()
    if (files.length === 0) {
      this.showPlaceholder()
      return
    }

    this.hidePlaceholder()
    files.forEach((file) => this.appendPreview(file))
    this.updateCtaLabel(files.length)
  }

  submit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
    }
    if (!this.hasOverlayTarget) return

    this.overlayTarget.classList.remove("hidden")
    requestAnimationFrame(() => {
      this.overlayTarget.classList.remove("opacity-0")
    })
    this.startProgress()
  }

  disconnect() {
    this.clearTimers()
    this.clearPreviews()
  }

  // private

  appendPreview(file) {
    const url = URL.createObjectURL(file)
    const img = document.createElement("img")
    img.src = url
    img.className = "h-20 w-20 object-cover rounded-xl ring-1 ring-warm-200 dark:ring-gray-700"
    img.onload = () => URL.revokeObjectURL(url)
    this.previewsTarget.appendChild(img)
  }

  updateCtaLabel(count) {
    if (!this.hasSubmitLabelTarget) return
    const noun = count === 1 ? "photo" : "photos"
    this.submitLabelTarget.textContent = `Scan ${count} ${noun} →`
  }

  hidePlaceholder() {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")
    if (this.hasPreviewsTarget) this.previewsTarget.classList.remove("hidden")
  }

  showPlaceholder() {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove("hidden")
    if (this.hasPreviewsTarget) this.previewsTarget.classList.add("hidden")
    if (this.hasSubmitLabelTarget) this.submitLabelTarget.textContent = "Scan inventory"
  }

  clearPreviews() {
    if (!this.hasPreviewsTarget) return
    while (this.previewsTarget.firstChild) {
      this.previewsTarget.removeChild(this.previewsTarget.firstChild)
    }
  }

  startProgress() {
    this.clearTimers()
    this.timers = LOADING_STEPS.map(({ text, delay }) =>
      setTimeout(() => {
        if (this.hasOverlayTextTarget) this.overlayTextTarget.textContent = text
      }, delay)
    )
    // After 14s, surface the "feel free to navigate away" hint.
    this.timers.push(setTimeout(() => {
      if (this.hasSlowTarget) this.slowTarget.classList.remove("hidden")
    }, 14000))
  }

  clearTimers() {
    (this.timers || []).forEach(clearTimeout)
    this.timers = []
  }
}
